import { Link } from "react-router-dom";
import {
    ArrowRight,
    ArrowUpRight,
    Sparkles,
    Target,
    BarChart3,
    Search,
    Megaphone,
    Database,
    Bot,
    TrendingUp,
    Zap,
} from "lucide-react";
import { services, principles, problems, aiSolutions, caseStudies, platformStack } from "@/data/content";
import { WALID_PHOTO_NIGHT, WALID_PHOTO_NIGHT_SRCSET, WALID_PHOTO_NIGHT_SIZES } from "@/data/assets";
import { useSEO, SITE } from "@/lib/seo";

const SERVICE_ICONS = {
    "performance-marketing-strategy": Target,
    "seo-organic-visibility": Search,
    "ppc-paid-media": Megaphone,
    "social-media-marketing": Sparkles,
    "marketing-analytics-reporting": BarChart3,
    "google-marketing-platform": Database,
};

function MiniChart() {
    // Hand-drawn area chart resembling alphorithm snapshot
    return (
        <svg
            viewBox="0 0 400 100"
            className="w-full h-24 md:h-28"
            preserveAspectRatio="none"
        >
            <defs>
                <linearGradient id="chartFill" x1="0" x2="0" y1="0" y2="1">
                    <stop offset="0%" stopColor="rgba(31,184,77,0.35)" />
                    <stop offset="100%" stopColor="rgba(31,184,77,0)" />
                </linearGradient>
            </defs>
            <path
                d="M0,80 L25,72 L55,76 L85,65 L115,68 L145,55 L175,58 L210,48 L240,42 L275,46 L310,32 L345,28 L380,22 L400,15"
                fill="none"
                stroke="#1fb84d"
                strokeWidth="2.5"
                strokeLinejoin="round"
                strokeLinecap="round"
            />
            <path
                d="M0,80 L25,72 L55,76 L85,65 L115,68 L145,55 L175,58 L210,48 L240,42 L275,46 L310,32 L345,28 L380,22 L400,15 L400,100 L0,100 Z"
                fill="url(#chartFill)"
            />
            {/* dotted baseline */}
            <line
                x1="0"
                y1="95"
                x2="400"
                y2="95"
                stroke="#d8d6cf"
                strokeWidth="1"
                strokeDasharray="3 4"
            />
        </svg>
    );
}

function Sparkline({ data, trend = "up" }) {
    if (!data || data.length === 0) return null;
    const max = Math.max(...data);
    const min = Math.min(...data);
    const range = max - min || 1;
    const w = 200;
    const h = 48;
    const stepX = w / (data.length - 1);
    const points = data
        .map((v, i) => `${i * stepX},${h - ((v - min) / range) * h}`)
        .join(" ");
    const stroke = trend === "down" ? "#1fb84d" : "#1fb84d";
    return (
        <svg
            viewBox={`0 0 ${w} ${h}`}
            className="w-full h-10"
            preserveAspectRatio="none"
        >
            <polyline
                points={points}
                fill="none"
                stroke={stroke}
                strokeWidth="2"
                strokeLinejoin="round"
                strokeLinecap="round"
            />
        </svg>
    );
}

export default function Home() {
    useSEO({
        title: "Walid Cheikh — Performance Marketing & AI Consultant | Dubai",
        description: "Dubai-based consultant building performance marketing systems and AI workflows for businesses. Paid media, SEO, CRM, analytics — engineered around acquisition cost, conversion and revenue.",
        canonical: `${SITE}/`,
    });
    return (
        <div data-testid="home-page">
            {/* HERO */}
            <section className="relative overflow-hidden">
                <div className="absolute inset-0 bg-dotted opacity-50 pointer-events-none" />
                <div
                    className="spot top-0 right-0 w-[700px] h-[700px]"
                    style={{
                        background:
                            "radial-gradient(circle, rgba(31,184,77,0.12) 0%, rgba(31,184,77,0) 70%)",
                    }}
                />

                <div className="container-page relative z-10 pt-10 md:pt-20 pb-24 md:pb-36">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center min-h-[60vh]">
                        {/* LEFT — Headline */}
                        <div className="lg:col-span-7 order-2 lg:order-1">
                            <h1
                                className="font-display text-[clamp(2.5rem,6vw,5.75rem)] leading-[1.02] tracking-[-0.035em] text-[color:var(--ink)] anim-fade-up"
                                data-testid="hero-headline"
                            >
                                Turn marketing spend into{" "}
                                <span className="brand-emph">measurable</span>,{" "}
                                <span className="brand-emph">funded</span> growth.
                            </h1>

                            <p className="mt-8 text-lg md:text-xl text-[color:var(--text-muted)] leading-relaxed max-w-xl anim-fade-up delay-1">
                                I'm{" "}
                                <strong className="text-[color:var(--ink)] font-semibold">
                                    Walid Cheikh
                                </strong>{" "}
                                — Dubai-based consultant building performance marketing
                                systems for businesses. Paid media, SEO, CRM, analytics, and
                                AI workflows engineered around acquisition cost, conversion
                                and revenue.
                            </p>

                            <div className="flex flex-wrap items-center gap-3 mt-10 anim-fade-up delay-2">
                                <Link
                                    to="/contact"
                                    className="btn-primary"
                                    data-testid="hero-primary-cta"
                                >
                                    Book a Call <ArrowUpRight size={14} />
                                </Link>
                                <Link
                                    to="/growth-audit"
                                    className="btn-secondary"
                                    data-testid="hero-secondary-cta"
                                >
                                    Request Growth Audit <ArrowUpRight size={14} />
                                </Link>
                            </div>
                        </div>

                        {/* RIGHT — Photo + floating snapshot card overlay */}
                        <div className="lg:col-span-5 order-1 lg:order-2 anim-fade-up delay-2 mb-44 md:mb-0">
                            <div className="relative">
                                {/* Decorative offset shadow */}
                                <div className="absolute inset-0 translate-x-3 translate-y-3 rounded-xl bg-[color:var(--brand-soft)] border border-[color:var(--brand)]/30 -z-10" />

                                {/* Night portrait of Walid */}
                                <div className="photo-frame aspect-[4/5] shadow-2xl shadow-black/25 border-[color:var(--ink)]">
                                    <img
                                        src={WALID_PHOTO_NIGHT}
                                        srcSet={WALID_PHOTO_NIGHT_SRCSET}
                                        sizes={WALID_PHOTO_NIGHT_SIZES}
                                        width={800}
                                        height={1000}
                                        alt="Walid Cheikh — Performance Marketing & AI Consultant"
                                        loading="eager"
                                        fetchPriority="high"
                                        decoding="async"
                                    />
                                </div>

                                {/* Floating snapshot card — same overlap on every viewport. On mobile, pushed further down so it clears the face. */}
                                <div className="absolute -bottom-40 left-2 md:-bottom-8 md:-left-8 right-2 md:right-auto md:w-[80%] bg-[color:var(--surface-2)] border border-[color:var(--ink)] rounded-xl p-5 shadow-2xl shadow-black/20">
                                    <div className="flex items-center justify-between mb-3">
                                        <div className="inline-flex items-center gap-2">
                                            <span className="w-2 h-2 rounded-full bg-[color:var(--brand)] live-dot"></span>
                                            <span className="mono-label">
                                                Live · Performance Snapshot
                                            </span>
                                        </div>
                                        <Zap
                                            size={14}
                                            className="text-[color:var(--brand-deep)]"
                                        />
                                    </div>
                                    <div className="mono-label !text-[10px]">
                                        Conversion uplift
                                    </div>
                                    <div className="mt-1 flex items-baseline gap-2">
                                        <div className="font-display text-4xl md:text-5xl text-[color:var(--ink)] tracking-tighter">
                                            +42.7%
                                        </div>
                                        <TrendingUp
                                            size={18}
                                            className="text-[color:var(--brand-deep)]"
                                        />
                                    </div>
                                    <div className="mt-3">
                                        <MiniChart />
                                    </div>
                                    <div className="grid grid-cols-3 gap-3 mt-2 pt-4 border-t border-[color:var(--line)]">
                                        {[
                                            ["Qualified leads", "+38%"],
                                            ["Avg deal", "$24k"],
                                            ["Time to MQL", "2.4d"],
                                        ].map(([k, v]) => (
                                            <div key={k}>
                                                <div className="mono-label !text-[9px]">
                                                    {k}
                                                </div>
                                                <div className="font-display text-sm md:text-base text-[color:var(--ink)] mt-1 inline-flex items-center gap-1">
                                                    {v}
                                                    <TrendingUp
                                                        size={10}
                                                        className="text-[color:var(--brand-deep)]"
                                                    />
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="hidden lg:flex items-center gap-3 mt-10 text-[color:var(--text-dim)] mono-label">
                        <span>↓</span>
                        <span>Scroll to explore</span>
                    </div>
                </div>
            </section>

            {/* MARQUEE — platform / tooling trust strip */}
            <section className="border-y border-[color:var(--line)] overflow-hidden py-6 bg-[color:var(--surface)]">
                <div className="container-page mb-3 flex items-center justify-between gap-6">
                    <div className="mono-label">
                        Platforms & tooling I operate in
                    </div>
                    <div className="mono-label hidden md:block !text-[10px] text-[color:var(--text-dim)]">
                        20+ marketing &amp; data systems
                    </div>
                </div>
                <div className="flex marquee-track whitespace-nowrap text-[color:var(--text-muted)] font-display text-lg md:text-xl font-bold tracking-tight">
                    {[...Array(2)].map((_, ix) => (
                        <div key={ix} className="flex gap-10 px-6 items-center">
                            {platformStack.map((t) => (
                                <span
                                    key={t + ix}
                                    className="flex items-center gap-10 opacity-80"
                                    data-testid={`platform-${t.toLowerCase().replace(/\s+/g, "-")}`}
                                >
                                    {t}
                                    <span className="text-[color:var(--brand)] text-2xl">
                                        ◆
                                    </span>
                                </span>
                            ))}
                        </div>
                    ))}
                </div>
            </section>

            {/* WHY ME PILLARS */}
            <section className="container-page py-24 md:py-28">
                <div className="grid grid-cols-1 md:grid-cols-12 gap-8 mb-14">
                    <div className="md:col-span-7">
                        <div className="overline mb-5">Why work with me</div>
                        <h2 className="font-display text-4xl md:text-6xl leading-[1.02] text-[color:var(--ink)]">
                            Proven digital growth{" "}
                            <span className="brand-emph">expertise</span>.
                        </h2>
                    </div>
                    <div className="md:col-span-4 md:col-start-9 md:pt-6">
                        <p className="text-[color:var(--text-muted)] text-base md:text-lg leading-relaxed">
                            Built for businesses that want real outcomes — not vanity
                            metrics. I optimise for what you actually monetise:{" "}
                            <strong className="text-[color:var(--ink)]">
                                qualified pipeline, conversion rate, and lifetime value
                            </strong>
                            .
                        </p>
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-px border border-[color:var(--line)] rounded-lg overflow-hidden bg-[color:var(--line)]">
                    {[
                        {
                            icon: BarChart3,
                            tag: "Pillar 01",
                            title: "Data-Driven Decision Making",
                            text: "Every decision is powered by performance data and funnel analytics. I track conversion at every stage — top-of-funnel, MQL, SQL, opportunity, close — and optimise continuously using structured testing and real-time insights. Your marketing budget is treated like an investment with measurable payback.",
                        },
                        {
                            icon: Target,
                            tag: "Pillar 02",
                            title: "Operator-Centric Model",
                            text: "I work like an extension of your growth team. Transparent reporting, fast iteration cycles, and a clear plan that aligns marketing with your compliance boundaries, GEOs, and business goals.",
                        },
                    ].map((p) => (
                        <div
                            key={p.tag}
                            className="bg-[color:var(--bg)] p-8 md:p-10"
                        >
                            <p.icon
                                size={28}
                                className="text-[color:var(--brand-deep)] mb-6"
                            />
                            <div className="overline mb-3">{p.tag}</div>
                            <h3 className="font-display text-2xl md:text-3xl text-[color:var(--ink)]">
                                {p.title}
                            </h3>
                            <p className="text-[color:var(--text-muted)] mt-4 leading-relaxed">
                                {p.text}
                            </p>
                        </div>
                    ))}
                </div>

                {/* KPI strip */}
                <div className="mt-px grid grid-cols-2 md:grid-cols-4 gap-px border border-[color:var(--line)] border-t-0 rounded-b-lg overflow-hidden bg-[color:var(--line)]">
                    {[
                        ["Projects", "120+", "Completed"],
                        ["Clients", "48+", "Across verticals"],
                        ["Experience", "16+", "Years in digital"],
                        ["Rating", "4.7+", "Client ratings"],
                    ].map(([k, v, sub]) => (
                        <div
                            key={k}
                            className="bg-[color:var(--bg)] p-6 md:p-8"
                        >
                            <div className="overline">{k}</div>
                            <div className="font-display text-4xl md:text-5xl text-[color:var(--ink)] mt-3 tracking-tighter">
                                {v}
                            </div>
                            <div className="mono-label !text-[10px] mt-2">{sub}</div>
                        </div>
                    ))}
                </div>
            </section>

            {/* SERVICES */}
            <section className="container-page py-24 md:py-28" id="services">
                <div className="grid grid-cols-1 md:grid-cols-12 gap-8 mb-14">
                    <div className="md:col-span-7">
                        <div className="overline mb-5">Services</div>
                        <h2 className="font-display text-4xl md:text-6xl leading-[1.02] text-[color:var(--ink)]">
                            High-impact marketing services built to{" "}
                            <span className="brand-emph">scale</span>.
                        </h2>
                    </div>
                    <div className="md:col-span-4 md:col-start-9 md:pt-6">
                        <p className="text-[color:var(--text-muted)] text-base md:text-lg leading-relaxed">
                            Built to improve the metrics that matter most: qualified pipeline,
                            conversion rate, deal velocity, and customer lifetime value.
                        </p>
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px border border-[color:var(--line)] rounded-lg overflow-hidden bg-[color:var(--line)]">
                    {services.map((s, i) => {
                        const Icon = SERVICE_ICONS[s.slug] || Target;
                        return (
                            <Link
                                key={s.slug}
                                to={`/services#${s.slug}`}
                                className="group flex flex-col bg-[color:var(--bg)] p-7 md:p-8 hover:bg-[color:var(--surface)] transition"
                                data-testid={`home-service-${s.slug}`}
                            >
                                <div className="flex items-start justify-between mb-6">
                                    <Icon
                                        size={26}
                                        className="text-[color:var(--ink)]"
                                        strokeWidth={1.5}
                                    />
                                    <span className="mono-label">0{i + 1}</span>
                                </div>
                                <h3 className="font-display text-xl md:text-2xl text-[color:var(--ink)] tracking-tight">
                                    {s.title}
                                </h3>
                                <p className="text-sm md:text-base text-[color:var(--text-muted)] leading-relaxed mt-3 flex-1">
                                    {s.short}
                                </p>
                                <div className="mt-6 inline-flex items-center gap-2 mono-label group-hover:text-[color:var(--ink)] transition">
                                    Learn more
                                    <ArrowUpRight size={14} className="card-arrow" />
                                </div>
                            </Link>
                        );
                    })}
                </div>
            </section>

            {/* SOLUTIONS / PROBLEMS — dark editorial panel */}
            <section className="bg-[color:var(--ink)] text-white relative overflow-hidden">
                <div className="container-page py-24 md:py-28 relative">
                    <div className="grid grid-cols-1 md:grid-cols-12 gap-8 mb-14">
                        <div className="md:col-span-7">
                            <div
                                className="overline mb-5"
                                style={{ color: "var(--brand-bright)" }}
                            >
                                Solutions
                            </div>
                            <h2 className="font-display text-4xl md:text-6xl leading-[1.02]">
                                Proven strategies for{" "}
                                <span style={{ color: "var(--brand-bright)" }}>
                                    revenue growth
                                </span>
                                .
                            </h2>
                        </div>
                        <div className="md:col-span-4 md:col-start-9 md:pt-6">
                            <p className="text-zinc-400 text-base md:text-lg leading-relaxed">
                                I solve the problems most businesses face when growth stalls
                                or CAC becomes unstable.
                            </p>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-px border border-white/10 rounded-lg overflow-hidden bg-white/5">
                        {problems.map((p, i) => (
                            <div
                                key={p.title}
                                className="bg-[color:var(--ink)] p-7 md:p-8 group hover:bg-[#161616] transition"
                            >
                                <div className="flex items-start justify-between mb-5">
                                    <span
                                        className="mono-label"
                                        style={{ color: "rgba(255,255,255,0.5)" }}
                                    >
                                        PRB-0{i + 1}
                                    </span>
                                    <ArrowUpRight
                                        size={16}
                                        className="text-zinc-600 group-hover:text-[color:var(--brand-bright)] transition"
                                    />
                                </div>
                                <h3 className="font-display text-xl md:text-2xl text-white tracking-tight">
                                    {p.title}
                                </h3>
                                <p className="text-zinc-400 text-sm md:text-base mt-3 leading-relaxed">
                                    {p.text}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CASE STUDIES — anonymised results panel */}
            <section className="container-page py-24 md:py-28" id="results">
                <div className="grid grid-cols-1 md:grid-cols-12 gap-8 mb-14">
                    <div className="md:col-span-7">
                        <div className="overline mb-5">Selected results</div>
                        <h2 className="font-display text-4xl md:text-6xl leading-[1.02] text-[color:var(--ink)]">
                            Numbers from real{" "}
                            <span className="brand-emph">engagements</span>.
                        </h2>
                    </div>
                    <div className="md:col-span-4 md:col-start-9 md:pt-6">
                        <p className="text-[color:var(--text-muted)] text-base md:text-lg leading-relaxed">
                            Client names withheld under NDA. The numbers are real —
                            outcomes from the regulated, fintech and DTC programmes I've
                            led.
                        </p>
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-px border border-[color:var(--line)] rounded-lg overflow-hidden bg-[color:var(--line)]">
                    {caseStudies.map((c, idx) => (
                        <Link
                            key={c.slug}
                            to={`/results/${c.slug}`}
                            className="bg-[color:var(--bg)] p-7 md:p-9 flex flex-col group hover:bg-[color:var(--surface)] transition"
                            data-testid={`case-study-${c.slug}`}
                        >
                            <div className="flex items-start justify-between gap-4 mb-5">
                                <div>
                                    <div className="overline">{c.sector}</div>
                                    <div className="mono-label !text-[10px] mt-1 text-[color:var(--text-dim)]">
                                        {c.geo} · {c.period}
                                    </div>
                                </div>
                                <span className="mono-label">
                                    CS-0{idx + 1}
                                </span>
                            </div>

                            <h3 className="font-display text-xl md:text-2xl text-[color:var(--ink)] tracking-tight leading-snug">
                                {c.headline}
                            </h3>

                            <p className="text-sm text-[color:var(--text-muted)] mt-4 leading-relaxed">
                                {c.challenge}
                            </p>

                            <div className="mt-5 mb-5">
                                <Sparkline data={c.chart} trend={c.metrics[0].trend} />
                            </div>

                            <div className="grid grid-cols-2 gap-4 pt-5 border-t border-[color:var(--line)]">
                                {c.metrics.map((m) => (
                                    <div key={m.label}>
                                        <div className="mono-label !text-[9px]">
                                            {m.label}
                                        </div>
                                        <div className="font-display text-lg md:text-xl text-[color:var(--ink)] mt-1 inline-flex items-center gap-1.5">
                                            {m.value}
                                            {m.trend === "up" ? (
                                                <TrendingUp
                                                    size={12}
                                                    className="text-[color:var(--brand-deep)]"
                                                />
                                            ) : m.trend === "down" ? (
                                                <ArrowRight
                                                    size={12}
                                                    className="text-[color:var(--brand-deep)] -rotate-45"
                                                />
                                            ) : null}
                                        </div>
                                    </div>
                                ))}
                            </div>

                            <ul className="mt-5 space-y-2">
                                {c.approach.slice(0, 3).map((a) => (
                                    <li
                                        key={a}
                                        className="text-xs md:text-sm text-[color:var(--text-muted)] flex gap-2"
                                    >
                                        <span className="text-[color:var(--brand-deep)] mt-0.5">
                                            →
                                        </span>
                                        <span>{a}</span>
                                    </li>
                                ))}
                            </ul>
                            <div className="mt-6 inline-flex items-center gap-2 mono-label group-hover:text-[color:var(--ink)] transition">
                                Read full case study
                                <ArrowUpRight size={14} className="card-arrow" />
                            </div>
                        </Link>
                    ))}
                </div>

                <div className="mt-10 flex flex-wrap items-center gap-3">
                    <Link
                        to="/results"
                        className="btn-secondary"
                        data-testid="home-results-link"
                    >
                        See all case studies <ArrowUpRight size={14} />
                    </Link>
                    <Link
                        to="/growth-audit"
                        className="btn-primary"
                        data-testid="case-studies-cta"
                    >
                        Get my growth audit <ArrowUpRight size={14} />
                    </Link>
                    <span className="mono-label !text-[10px] text-[color:var(--text-dim)]">
                        Confidential · 30-minute strategy review
                    </span>
                </div>
            </section>

            {/* PROCESS */}
            <section className="container-page py-24 md:py-28">
                <div className="grid grid-cols-1 md:grid-cols-12 gap-8 mb-14">
                    <div className="md:col-span-7">
                        <div className="overline mb-5">How I work</div>
                        <h2 className="font-display text-4xl md:text-6xl leading-[1.02] text-[color:var(--ink)]">
                            My process,{" "}
                            <span className="brand-emph">your growth</span>.
                        </h2>
                    </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px border border-[color:var(--line)] rounded-lg overflow-hidden bg-[color:var(--line)]">
                    {[
                        {
                            n: "01",
                            t: "Discovery & Audit",
                            d: "I map your business model, GEO priorities, compliance constraints, and audit the full funnel — from acquisition to conversion drop-offs to revenue events.",
                        },
                        {
                            n: "02",
                            t: "Strategy Development",
                            d: "Business-specific growth plan built on funnel economics: channel strategy, creative angles, landing improvements, CRM lifecycle, and measurement plan.",
                        },
                        {
                            n: "03",
                            t: "Execution",
                            d: "I execute campaigns, optimise landing experiences, implement tracking, and launch lifecycle CRM with a testing roadmap that compounds.",
                        },
                        {
                            n: "04",
                            t: "Monitoring & Optimisation",
                            d: "Daily tracking, weekly iteration. Optimisations prioritised by impact on pipeline, conversion and revenue contribution.",
                        },
                    ].map((s) => (
                        <div
                            key={s.n}
                            className="bg-[color:var(--bg)] p-7 md:p-8 flex flex-col"
                        >
                            <div className="font-display text-2xl md:text-3xl text-[color:var(--brand-deep)] mb-4">
                                {s.n}
                            </div>
                            <h3 className="font-display text-lg md:text-xl text-[color:var(--ink)] tracking-tight">
                                {s.t}
                            </h3>
                            <p className="text-sm text-[color:var(--text-muted)] mt-3 leading-relaxed">
                                {s.d}
                            </p>
                        </div>
                    ))}
                </div>
            </section>

            {/* AI BLOCK */}
            <section className="container-page pb-24 md:pb-28">
                <div className="relative overflow-hidden rounded-xl border border-[color:var(--line)] bg-[color:var(--surface)] p-8 md:p-14">
                    <div
                        className="spot -left-20 -bottom-20 w-[500px] h-[500px]"
                        style={{
                            background:
                                "radial-gradient(circle, rgba(31,184,77,0.12) 0%, rgba(31,184,77,0) 60%)",
                        }}
                    />
                    <div className="relative grid grid-cols-1 md:grid-cols-12 gap-10 items-center">
                        <div className="md:col-span-7">
                            <div className="chip">
                                <Bot size={12} /> AI Solutions
                            </div>
                            <h2 className="font-display text-3xl md:text-5xl leading-[1.05] mt-5 text-[color:var(--ink)]">
                                AI workflows that{" "}
                                <span className="brand-emph">ship revenue</span>.
                            </h2>
                            <p className="text-[color:var(--text-muted)] mt-5 max-w-xl leading-relaxed text-base md:text-lg">
                                Pragmatic AI for marketing teams: automation, agents,
                                content, reporting and compliant lead operations for
                                regulated industries.
                            </p>
                            <Link
                                to="/ai-solutions"
                                className="btn-primary mt-8"
                                data-testid="home-ai-cta"
                            >
                                Explore AI Solutions <ArrowUpRight size={14} />
                            </Link>
                        </div>
                        <div className="md:col-span-5 grid grid-cols-2 gap-3">
                            {aiSolutions.slice(0, 4).map((s) => (
                                <Link
                                    key={s.slug}
                                    to={`/ai-solutions/${s.slug}`}
                                    className="rounded-lg border border-[color:var(--line-strong)] bg-[color:var(--surface-2)] p-4 hover:border-[color:var(--ink)] transition group"
                                >
                                    <span className="tag mb-3">{s.tag}</span>
                                    <div className="font-display text-sm md:text-base text-[color:var(--ink)] mt-2 leading-snug">
                                        {s.title}
                                    </div>
                                    <ArrowUpRight
                                        size={16}
                                        className="text-[color:var(--text-dim)] mt-3 group-hover:text-[color:var(--brand-deep)] transition"
                                    />
                                </Link>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* FINAL CTA */}
            <section className="bg-[color:var(--bg-warm)] border-y border-[color:var(--line)]">
                <div className="container-page py-20 md:py-28">
                    <div className="overline mb-4">Let's build</div>
                    <h2 className="font-display text-4xl md:text-6xl leading-[1.02] text-[color:var(--ink)] max-w-4xl">
                        Proven marketing solutions designed to drive{" "}
                        <span className="brand-emph">growth</span>.
                    </h2>
                    <p className="mt-6 text-base md:text-lg text-[color:var(--text-muted)] max-w-2xl leading-relaxed">
                        Maximise your digital impact — where strategy meets execution for
                        measurable funnel results.
                    </p>
                    <div className="flex flex-wrap items-center gap-3 mt-8">
                        <Link
                            to="/contact"
                            className="btn-primary"
                            data-testid="footer-cta-button"
                        >
                            Contact me <ArrowUpRight size={14} />
                        </Link>
                        <Link to="/services" className="btn-secondary">
                            My services <ArrowUpRight size={14} />
                        </Link>
                    </div>
                </div>
            </section>
        </div>
    );
}
